import React, { useState, useRef, useEffect } from "react";
import { Row, Col, Spinner, Button } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import DOMPurify from "dompurify";
import AsyncSelect from "react-select/async";

import Back from "../../assets/svg/back.svg";
import Profile from "../../assets/png/profile.png";

import {
  createContactApi,
  updateContactApi,
  getContactDetailsApi,
  uploadAttachmentsApi,
} from "./contacts-api";
import { apiCallGeneric } from "../service/api-service";
import "./contacts-create.css";
import Delete from "../../assets/svg/delete-po.svg";
import Add from "../../assets/svg/add_new.svg";
import SearchWithAPI from "../common/searchbox";
import SearchWithFreeTextAPI from "../common/searchbox-free-text";
import { Toast, ToastContainer } from "react-bootstrap";
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
  FaFile,
  FaDownload,
} from "react-icons/fa";
import {
  sanitizeInput,
  sanitizeTextarea,
  validateUrl,
  sanitizeEmail,
  sanitizePhoneNumber,
  sanitizeNumericInput,
  validateFileSize,
  validateFileType,
  sanitizeFileName,
  downloadBlobFile,
} from "../common/security-utils.js";

const ErrorMessage = ({ message }) => {
  if (!message) return null;
  return (
    <span
      style={{
        color: "#ff4444",
        fontSize: "12px",
        fontWeight: "400",
        display: "block",
        marginTop: "4px",
      }}
    >
      {message}
    </span>
  );
};

// Helper function to get file icon based on type
const getFileIcon = (fileName) => {
  const ext = fileName?.split(".").pop()?.toLowerCase();
  const iconStyle = { fontSize: "24px" };

  switch (ext) {
    case "pdf":
      return <FaFilePdf style={{ ...iconStyle, color: "#FF0000" }} />;
    case "doc":
    case "docx":
      return <FaFileWord style={{ ...iconStyle, color: "#2B579A" }} />;
    case "xls":
    case "xlsx":
      return <FaFileExcel style={{ ...iconStyle, color: "#217346" }} />;
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return <FaFileImage style={{ ...iconStyle, color: "#3D4AF5" }} />;
    default:
      return <FaFile style={{ ...iconStyle, color: "#666666" }} />;
  }
};

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const ContactsCreate = () => {
  const apiAccUrl = process.env.REACT_APP_ADMIN_API_URL;
  const apiUrl = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();
  const location = useLocation();

  const { id, moduleDetail, contactFrom, prefill } = location.state || {};

  // Reusable error notification helper
  const showError = (message) => {
    setSnackbarMessage(message);
    setSnackbarStatus(false);
    handleSnackbarShow();
  };

  // Reusable success notification helper
  const showSuccess = (message) => {
    setSnackbarMessage(message);
    setSnackbarStatus(true);
    handleSnackbarShow();
  };

  // Reusable file validation helper
  const validateFile = (
    file,
    allowedTypes,
    maxSizeMB,
    fileTypeName = "file",
  ) => {
    if (!validateFileType(file, allowedTypes)) {
      showError(`Invalid file type. Only ${fileTypeName} are allowed.`);
      return false;
    }
    if (!validateFileSize(file, maxSizeMB)) {
      showError(`File size must not exceed ${maxSizeMB}MB.`);
      return false;
    }
    return true;
  };

  // Reusable safe option value extractor
  const getOptionValue = (option, sanitizeFn = sanitize) => {
    if (!option?.value) return null;
    const sanitized = sanitizeFn(option.value);
    return sanitized || null;
  };

  // Reusable safe option value as integer
  const getOptionValueInt = (option) => {
    const value = getOptionValue(option);
    return value ? parseInt(value) : null;
  };

  // Download attachment function
  const downloadAttachment = async (file) => {
    if (file) {
      const success = await downloadBlobFile(file, "download");
      if (!success) {
        showError("Download failed. Please try again.");
      }
    }
  };

  useEffect(() => {
    if (id != null) {
      setContactId(id);
      getContactDetails();
    }
    if (contactFrom && contactFrom === "Lead" && moduleDetail) {
      setLinkLeads(true);
      setLeadOptions([moduleDetail]);
    }
    if (contactFrom && contactFrom === "Account" && moduleDetail) {
      setLinkAccounts(true);
      setAccountOptions([moduleDetail]);
    }
    if (prefill) {
      if (prefill.first_name) setFirstName(prefill.first_name);
      if (prefill.last_name) setLastName(prefill.last_name);
      if (prefill.designation) setDesignationText(prefill.designation);
      if (prefill.mobile) setPhones([{ country: "+91", number: prefill.mobile, type: "" }]);
      if (prefill.email) setEmails([{ email: prefill.email, type: "" }]);
    }
  }, []);
  const [contactId, setContactId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [sourceOption, setSourceOption] = useState();
  const [designationText, setDesignationText] = useState("");
  const [designationOption, setDesignationOption] = useState();
  const [departmentText, setDepartmentText] = useState("");
  const [departmentOption, setDepartmentOption] = useState();
  const [industryOption, setIndustryOption] = useState();
  // Contact-type is no longer exclusive: a contact can be linked to any
  // number of Leads and/or Accounts at once. linkLeads/linkAccounts are the
  // "show this picker" toggles; leadOptions/accountOptions hold the
  // multi-selected {value,label} entries themselves.
  const [linkLeads, setLinkLeads] = useState(false);
  const [linkAccounts, setLinkAccounts] = useState(false);
  const [leadOptions, setLeadOptions] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [addressTitle, setAddressTitle] = useState("");
  const [address, setAddress] = useState("");
  const [area, setArea] = useState("");
  const [cityOption, setCityOption] = useState();
  const [stateOption, setStateOption] = useState();
  const [countryOption, setCountryOption] = useState();
  const [zipCode, setZipCode] = useState("");
  const [notes, setNotes] = useState("");
  const [description, setDescription] = useState("");

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  const [validations, setValidations] = useState({
    first_name: "",
    last_name: "",
    company_name: "",
    lead_source: "",
    designation: "",
    department: "",
    industry_type: "",
    contact_type: "",
    account_id: "",
    address_title: "",
    address: "",
    area: "",
    city_id: "",
    state_id: "",
    country_id: "",
    zip_code: "",
    notes: "",
    description: "",
    profile_image: "",
    mobiles: "",
    emails: "",
    social_links: "",
    tags: "",
  });
  // repeatable fields
  const [phones, setPhones] = useState([
    { country: "+91", number: "", type: "" },
  ]);
  const [emails, setEmails] = useState([{ email: "", type: "" }]);
  const [socialLinks, setSocialLinks] = useState([{ title: "", url: "" }]);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");

  const addTag = () => {
    const value = sanitize(newTag).trim();
    if (!value) return;
    if (tags.includes(value)) {
      setNewTag("");
      return;
    }
    setTags((t) => [...t, value]);
    setNewTag("");
  };

  const removeTag = (idx) => setTags((t) => t.filter((_, i) => i !== idx));

  const [attachments, setAttachments] = useState([]);
  const [attachmentsUrl, setAttachmentsUrl] = useState([]);
  const [removedAttachments, setRemovedAttachments] = useState([]);

  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);

  const fileInputRef = useRef();
  const attachInputRef = useRef();
  const [isDragging, setIsDragging] = useState(false);

  const sanitize = (value) => DOMPurify.sanitize(value ?? "");

  const addPhone = () =>
    setPhones((p) => [...p, { country: "+91", number: "", type: "" }]);
  const removePhone = (idx) => setPhones((p) => p.filter((_, i) => i !== idx));
  const updatePhone = (idx, key, value) => {
    let val = value;
    if (key === "number") {
      val = value.replace(/[^0-9]/g, "").slice(0, 15);
    }
    setPhones((p) =>
      p.map((item, i) => {
        if (i === idx) {
          return { ...item, [key]: val };
        }
        if (key === "type" && val === "Primary" && item.type === "Primary") {
          return { ...item, type: "Secondary" };
        }
        return item;
      }),
    );
  };

  const addEmail = () => setEmails((e) => [...e, { email: "", type: "" }]);
  const removeEmail = (idx) => setEmails((e) => e.filter((_, i) => i !== idx));
  const updateEmail = (idx, key, value) => {
    let val = value;
    if (key === "email") {
      val = value.trim().toLowerCase();
    }
    setEmails((e) =>
      e.map((item, i) => {
        if (i === idx) {
          return { ...item, [key]: val };
        }
        if (key === "type" && val === "Primary" && item.type === "Primary") {
          return { ...item, type: "Secondary" };
        }
        return item;
      }),
    );
  };

  const addSocial = () => setSocialLinks((s) => [...s, { title: "", url: "" }]);
  const removeSocial = (idx) =>
    setSocialLinks((s) => s.filter((_, i) => i !== idx));
  const updateSocial = (idx, key, value) =>
    setSocialLinks((s) =>
      s.map((item, i) => (i === idx ? { ...item, [key]: value } : item)),
    );

  const onPhotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validateFile(f, allowedTypes, 5, "image files")) return;

    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const processFiles = (files) => {
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    for (const file of files) {
      if (
        !validateFile(
          file,
          allowedTypes,
          10,
          "images, PDF, Word, and Excel files",
        )
      ) {
        return;
      }
    }

    setAttachments((a) => [...a, ...files]);
  };

  const onAttachChange = (e) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    processFiles(files);
  };

  const removeAttachment = (idx) =>
    setAttachments((a) => a.filter((_, i) => i !== idx));

  const removeAttachmentUrl = (idx, id) => {
    setRemovedAttachments((a) => [...a, id]);
    setAttachmentsUrl((a) => a.filter((_, i) => i !== idx));
  };

  const submitContact = async () => {
    setIsSaving(true);
    let hasError = false;
    const validationTemp = {
      first_name: "",
      last_name: "",
      company_name: "",
      lead_source: "",
      designation: "",
      department: "",
      industry_type: "",
      contact_type: "",
      account_id: "",
      lead_id: "",
      address_title: "",
      address: "",
      area: "",
      city_id: "",
      state_id: "",
      country_id: "",
      zip_code: "",
      notes: "",
      description: "",
      profile_image: "",
      mobiles: "",
      emails: "",
      social_links: "",
      tags: "",
    };

    // First Name Validation (required)
    if (!firstName.trim()) {
      validationTemp.first_name = "First name is required";
      hasError = true;
    }

    // Last Name Validation (required)
    if (!lastName.trim()) {
      validationTemp.last_name = "Last name is required";
      hasError = true;
    }

    // Designation Validation (required)
    if (!designationText.trim()) {
      validationTemp.designation = "Designation is required";
      hasError = true;
    }

    // Validate Lead / Account selections if toggled
    if (linkLeads && leadOptions.length === 0) {
      validationTemp.lead_id = "Select at least one lead";
      hasError = true;
    }
    if (linkAccounts && accountOptions.length === 0) {
      validationTemp.account_id = "Select at least one account";
      hasError = true;
    }

    // Validate Emails
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const validEmails = emails.filter((e) => !!e.email && e.email.trim() !== "");
    if (validEmails.length === 0) {
      validationTemp.emails = "Email is required";
      hasError = true;
    } else {
      let emailErr = "";
      validEmails.forEach((e) => {
        const emailVal = (e.email || "").trim();
        if (!emailRegex.test(emailVal)) {
          emailErr = "Please enter a valid email address (e.g. user@example.com)";
        }
      });
      if (emailErr) {
        validationTemp.emails = emailErr;
        hasError = true;
      }
    }

    // Validate Mobiles
    const validPhones = phones.filter((p) => !!p.number && p.number.trim() !== "");
    if (validPhones.length === 0) {
      validationTemp.mobiles = "Mobile number is required";
      hasError = true;
    } else {
      let mobileErr = "";
      validPhones.forEach((p) => {
        const numVal = (p.number || "").replace(/[^0-9]/g, "");
        if (numVal.length < 7 || numVal.length > 18) {
          mobileErr = "Mobile number must be between 7 and 18 digits";
        }
      });
      if (mobileErr) {
        validationTemp.mobiles = mobileErr;
        hasError = true;
      }
    }

    if (hasError) {
      setValidations(validationTemp);
      showError("Please fill all required fields correctly.");
      setIsSaving(false);
      return;
    }

    const leadId = linkLeads && leadOptions.length > 0 ? (leadOptions[0]?.value ?? leadOptions[0]) : null;
    const accountId = linkAccounts && accountOptions.length > 0 ? (accountOptions[0]?.value ?? accountOptions[0]) : null;
    const contactType = linkAccounts && accountOptions.length > 0 ? "Account" : (linkLeads && leadOptions.length > 0 ? "Lead" : "");

    const cleanedMobiles = phones
      .filter((p) => !!p.number && p.number.trim() !== "")
      .map((p) => ({
        mobile_number: sanitize(p.number.trim()),
        type: sanitize(p.type) || "Primary",
      }));

    const cleanedEmails = emails
      .filter((e) => !!e.email && e.email.trim() !== "")
      .map((e) => ({
        email: sanitize(e.email.trim()),
        type: sanitize(e.type) || "Primary",
      }));

    const payload = {
      first_name: sanitize(firstName),
      last_name: sanitize(lastName),
      company_name: sanitize(company),
      lead_source: getOptionValue(sourceOption),
      designation: sanitize(designationText),
      department: sanitize(departmentText),
      industry_type: getOptionValue(industryOption),
      contact_type: contactType,
      lead_id: leadId,
      account_id: accountId,
      account_ids: linkAccounts ? accountOptions.map((o) => o?.value ?? o).filter(Boolean) : [],
      lead_ids: linkLeads ? leadOptions.map((o) => o?.value ?? o).filter(Boolean) : [],
      address_title: sanitize(addressTitle),
      address: sanitize(address),
      area: sanitize(area),
      city_id: getOptionValue(cityOption),
      state_id: getOptionValue(stateOption),
      country_id: getOptionValue(countryOption),
      zip_code: sanitize(zipCode),
      notes: sanitize(notes),
      description: sanitize(description),
      profile_image: photoPreview || "",
      mobiles: cleanedMobiles,
      emails: cleanedEmails,
      social_links: socialLinks.filter((s) => !!s.title || !!s.url).map((s) => ({
        name: sanitize(s.title),
        link: sanitize(s.url),
      })),
      tags: tags.filter((t) => !!t && t.trim() !== "").map((t) => ({
        tag: sanitize(t),
      })),
      removed_attachments: removedAttachments,
    };

    const parseBackendErrors = (errorObj) => {
      const map = {};
      if (errorObj && typeof errorObj === "object") {
        Object.entries(errorObj).forEach(([key, value]) => {
          const errVal = Array.isArray(value) && value.length > 0 ? value[0] : (typeof value === "string" ? value : "");
          if (key.startsWith("mobiles")) {
            map.mobiles = map.mobiles || errVal;
          } else if (key.startsWith("emails")) {
            map.emails = map.emails || errVal;
          } else if (key.startsWith("social_links")) {
            map.social_links = map.social_links || errVal;
          } else if (key.startsWith("tags")) {
            map.tags = map.tags || errVal;
          } else {
            map[key] = errVal;
          }
        });
      }
      return map;
    };

    try {
      let response;
      if (contactId != null) {
        response = await updateContactApi(payload, contactId);
      } else {
        response = await createContactApi(payload);
      }
      if (response?.status === 200 && response?.data?.success === true) {
        setSnackbarMessage(response.data.message || "Contact saved successfully");
        setSnackbarStatus(true);
        handleSnackbarShow();
        setContactId(response.data.id);

        if (attachments.length === 0) {
          navigate(-1);
        } else {
          uploadAttachment(response.data.id);
        }
      } else if (response?.status === 400 || response?.status === 422 || response?.data?.success === false) {
        setSnackbarMessage(response?.data?.message || "Validation error");
        setSnackbarStatus(false);
        handleSnackbarShow();

        if (response?.data?.error) {
          const validationMap = parseBackendErrors(response.data.error);
          setValidations((prev) => ({ ...prev, ...validationMap }));
        }
      } else {
        showError(response?.data?.message || "Failed to save contact");
      }
    } catch (err) {
      if (err?.response?.data?.error) {
        const validationMap = parseBackendErrors(err.response.data.error);
        setValidations((prev) => ({ ...prev, ...validationMap }));
      }
      showError(err?.response?.data?.message || "An error occurred while saving contact.");
    } finally {
      setIsSaving(false);
    }
  };
  const uploadAttachment = async (id) => {
    try {
      // Convert all File objects to base64
      const filePromises = attachments.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve({
              file_name: file.name,
              file_size: file.size,
              file_type: file.type,
              base64: e.target.result, // This includes "data:image/png;base64,..." prefix
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const base64Files = await Promise.all(filePromises);

      const payload = {
        attachments: base64Files.map((file) => file.base64),
      };

      const response = await uploadAttachmentsApi(id, payload);
      if (response.status === 200 && response.data.success === true) {
        setSnackbarMessage(response.data.message);
        setSnackbarStatus(response.data.success);
        handleSnackbarShow();
        navigate(-1);
      }
    } catch (error) {
      setSnackbarMessage("Failed to upload attachments");
      setSnackbarStatus(false);
      handleSnackbarShow();
    }
  };

  const getContactDetails = async () => {
    try {
      const response = await getContactDetailsApi(id);

      if (response.success === true && response.contact != null) {
        const contactDetails = response.contact;

        // Basic information
        setFirstName(contactDetails.first_name ?? "");
        setLastName(contactDetails.last_name ?? "");
        setCompany(contactDetails.companyname ?? "");
        setDesignationText(contactDetails.designation ?? "");
        setDepartmentText(contactDetails.department ?? "");

        // Linked accounts/leads (many-to-many)
        const accountIds = contactDetails.account_ids ?? [];
        const leadIds = contactDetails.lead_ids ?? [];
        setLinkAccounts(accountIds.length > 0);
        setAccountOptions(accountIds.map((a) => ({ value: String(a.value), label: a.label })));
        setLinkLeads(leadIds.length > 0);
        setLeadOptions(leadIds.map((l) => ({ value: String(l.value), label: l.label })));

        // Source dropdown
        if (
          contactDetails.source_value &&
          contactDetails.source_label &&
          contactDetails.source_value !== "0"
        ) {
          setSourceOption({
            value: contactDetails.source_value,
            label: contactDetails.source_label,
          });
        }

        // Industry dropdown
        if (
          contactDetails.industrial_value &&
          contactDetails.industrial_label
        ) {
          setIndustryOption({
            value: contactDetails.industrial_value,
            label: contactDetails.industrial_label,
          });
        }

        // Address information
        setAddressTitle(contactDetails.address_title ?? "");
        setAddress(contactDetails.address ?? "");
        setArea(contactDetails.area ?? "");
        setZipCode(contactDetails.postalcode ?? "");

        // City dropdown
        if (contactDetails.city_value && contactDetails.city_label) {
          setCityOption({
            value: contactDetails.city_value,
            label: contactDetails.city_label,
          });
        }

        // State dropdown
        if (contactDetails.state_value && contactDetails.state_label) {
          setStateOption({
            value: contactDetails.state_value,
            label: contactDetails.state_label,
          });
        }

        // Country dropdown
        if (contactDetails.country_value && contactDetails.country_label) {
          setCountryOption({
            value: contactDetails.country_value,
            label: contactDetails.country_label,
          });
        }

        // Notes and description
        setNotes(contactDetails.notes ?? "");
        setDescription(contactDetails.description ?? "");

        // Profile image
        if (contactDetails.profile) {
          setPhotoPreviewUrl(contactDetails.profile);
        }

        // Mobiles
        if (contactDetails.mobiles && contactDetails.mobiles.length > 0) {
          const mappedPhones = contactDetails.mobiles.map((mobile) => {
            // Extract country code and number from mobile_number
            const fullNumber = mobile.mobile_number || "";
            const match = fullNumber.match(/^(\+\d{1,3})(.+)$/);

            return {
              /* country: match ? match[1] : "+91", */
              number: /*  match ? match[2] :  */ fullNumber,
              type: mobile.type || "",
            };
          });
          setPhones(mappedPhones);
        }

        // Emails
        if (contactDetails.emails && contactDetails.emails.length > 0) {
          const mappedEmails = contactDetails.emails.map((emailObj) => ({
            email: emailObj.email || "",
            type: emailObj.type || "",
          }));
          setEmails(mappedEmails);
        }

        // Social links
        if (
          contactDetails.social_links &&
          contactDetails.social_links.length > 0
        ) {
          const mappedSocialLinks = contactDetails.social_links.map(
            (social) => ({
              title: social.name || "",
              url: social.link || "",
            }),
          );
          setSocialLinks(mappedSocialLinks);
        }

        // Tags
        if (contactDetails.tags && contactDetails.tags.length > 0) {
          const mappedTags = contactDetails.tags.map((tagObj) => tagObj.tag);
          setTags(mappedTags);
        }

        // Attachments
        if (
          contactDetails.attachments &&
          contactDetails.attachments.length > 0
        ) {
          setAttachmentsUrl(contactDetails.attachments);
        }
      }
    } catch (err) {
      // Error handled silently
    }
  };

  const handleSourceDropdownChange = (option) => {
    setSourceOption(option);
  };
  const handleIndustryDropdownChange = (option) => {
    setIndustryOption(option);
  };
  const multiselectCustomStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "#ffffff",
      borderRadius: "8px",
      border: "0.7px solid #E5E5E5",
      padding: "2px",
      fontWeight: "500",
      fontSize: "13px",
      color: "#212129",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#007bff" : state.isFocused ? "#e0e0e0" : "#fff",
      color: state.isSelected ? "#fff" : "#333",
      padding: 10,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#F6F7F8",
      color: "#212129",
      borderRadius: "5px",
      padding: "2px 5px",
    }),
    multiValueLabel: (provided) => ({ ...provided, color: "#212129" }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#212129",
      cursor: "pointer",
      ":hover": { backgroundColor: "red", color: "white" },
    }),
  };

  const loadLeadOptions = (inputValue, callback) => {
    setTimeout(async () => {
      try {
        const response = await apiCallGeneric("GET", apiUrl + "get_leads", null, { params: { term: inputValue || "" } });
        if (response?.status === 200 && response.data?.success) {
          callback((response.data.suggestionlist || []).map((item) => ({ value: String(item.value), label: String(item.label) })));
        } else {
          callback([]);
        }
      } catch {
        callback([]);
      }
    }, 400);
  };

  const loadAccountOptions = (inputValue, callback) => {
    setTimeout(async () => {
      try {
        const response = await apiCallGeneric("GET", apiUrl + "get_customers", null, { params: { term: inputValue || "" } });
        if (response?.status === 200 && response.data?.success) {
          callback((response.data.suggestionlist || []).map((item) => ({ value: String(item.value), label: String(item.label) })));
        } else {
          callback([]);
        }
      } catch {
        callback([]);
      }
    }, 400);
  };
  //City
  const handleCityDropdownChange = (option) => {
    setCityOption(option);
    setStateOption({ label: option.state_name, value: option.state_id });
    setCountryOption({ label: option.country_name, value: option.country_id });
  };

  //State

  const handleStateDropdownChange = (option) => {
    setStateOption(option);
    setCountryOption({ label: option.country_name, value: option.country_id });
    setCityOption(null);
  };

  //Country

  const handleCountryDropdownChange = (option) => {
    setCountryOption(option);
    setCityOption(null);
    setStateOption(null);
  };

  const handleDesignationDropdownChange = (option) => {
    setDesignationText(option.label);
    setDesignationOption(option);
  };

  const handleDesignationOnChange = (term) => {
    setDesignationText(term);
    setDesignationOption(null);
  };
  const handleDepartmentDropdownChange = (option) => {
    setDepartmentText(option.label);
    setDepartmentOption(option);
  };

  const handleDepartmentOnChange = (term) => {
    setDepartmentText(term);
    setDepartmentOption(null);
  };

  return (
    <div className="page-container contacts-page">
      <div className="contacts-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-3">
          <img
            src={Back}
            alt="back"
            className="back-icon"
            onClick={() => navigate(-1)}
          />
          <div className="page-title">New Contact</div>
        </div>

        <div>
          <Button
            variant="light"
            className="btn-cancel me-2"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            className="btn-save"
            onClick={submitContact}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" /> Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>

      <div className="row col-12  mt-3">
        <div className="col-md-8 col-sm-12">
          <div className="contact-card">
            <h5 className="card-title">Contact information</h5>

            <div className="upload-row d-flex align-items-center justify-content-between">
              <div className="upload-left d-flex align-items-center gap-3">
                <div className="photo-preview">
                  {photoPreview || photoPreviewUrl ? (
                    <img
                      src={photoPreview ?? photoPreviewUrl}
                      alt="Contact profile"
                    />
                  ) : (
                    <img src={Profile} alt="Default profile" />
                  )}
                </div>
                <div>
                  <div className="upload-label">Upload photo</div>
                  <div className="upload-sub">
                    An image of the person, it's best if it has the same length
                    and height
                  </div>
                </div>
              </div>

              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={onPhotoChange}
                  ref={fileInputRef}
                  style={{ display: "none" }}
                />
                <Button
                  className="choose-file"
                  variant="outline-primary"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose from files
                </Button>
              </div>
            </div>

            <Row className="mt-3">
              <Col md={6}>
                <label className="field-label">
                  First Name<span className="required">*</span>
                </label>
                <input
                  className="form-control"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) =>
                    setFirstName(sanitizeTextarea(e.target.value))
                  }
                />
                <ErrorMessage message={validations.first_name} />
              </Col>

              <Col md={6}>
                <label className="field-label">
                  Last Name<span className="required">*</span>
                </label>
                <input
                  className="form-control"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) =>
                    setLastName(sanitizeTextarea(e.target.value))
                  }
                />
                <ErrorMessage message={validations.last_name} />
              </Col>

              <Col md={6} className="mt-3">
                <label className="field-label">Company name</label>
                <input
                  className="form-control"
                  placeholder="Enter here..."
                  value={company}
                  onChange={(e) => setCompany(sanitizeTextarea(e.target.value))}
                />
                <ErrorMessage message={validations.company_name} />
              </Col>

              <Col md={6} className="mt-3">
                <label className="field-label">Source</label>
                <SearchWithAPI
                  onDropdownChange={handleSourceDropdownChange}
                  apiUrl={apiUrl + "lead/leadsource_autosuggestion"}
                  params={{}}
                  placeholder={"Select source"}
                  selectedOption={sourceOption}
                />
                <ErrorMessage message={validations.lead_source} />
              </Col>

              <Col md={12} className="mt-4">
                <div className="sub-card">
                  <div className="sub-card-title">
                    Mobile Number<span className="required">*</span>
                  </div>
                  {phones.map((p, idx) => (
                    <div
                      className="sub-row d-flex align-items-center"
                      key={idx}
                    >
                      <div className="mobile-input-wrapper" style={{ flex: 2 }}>
                        {/* <input
                          type="tel"
                          value={p.country}
                          className="country-code no-focus-border"
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^\+?\d{0,3}$/.test(val)) {
                              updatePhone(idx, "country", e.target.value);
                            }
                          }}
                        /> */}
                        <input
                          placeholder="Mobile number"
                          maxLength={15}
                          className="mobile-number no-focus-border"
                          value={p.number}
                          onChange={(e) =>
                            updatePhone(idx, "number", e.target.value)
                          }
                        />
                      </div>
                      <select
                        className="form-select"
                        style={{ flex: 1 }}
                        value={p.type}
                        onChange={(e) =>
                          updatePhone(idx, "type", e.target.value)
                        }
                      >
                        <option value="">Select</option>
                        <option value="Primary">Primary</option>
                        <option value="Secondary">Secondary</option>
                      </select>
                      <div className="ms-2">
                        {idx === 0 ? (
                          <Button
                            className="btn-style"
                            variant="outline-primary"
                            size="sm"
                            onClick={addPhone}
                          >
                            <img src={Add} alt="add" className="add-icon" />
                          </Button>
                        ) : (
                          <Button
                            className="btn-style "
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removePhone(idx)}
                          >
                            <img
                              src={Delete}
                              alt="delete"
                              className="delete-icon"
                            />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <ErrorMessage message={validations.mobiles} />
                </div>
              </Col>

              <Col md={12} className="mt-3">
                <div className="sub-card">
                  <div className="sub-card-title">
                    Email<span className="required">*</span>
                  </div>
                  {emails.map((em, idx) => (
                    <div
                      className="sub-row d-flex align-items-center"
                      key={idx}
                    >
                      <input
                        className="form-control"
                        value={em.email}
                        style={{ flex: 2 }}
                        onChange={(e) =>
                          updateEmail(idx, "email", e.target.value)
                        }
                        placeholder={"Enter here..."}
                      />
                      <select
                        className="form-select"
                        style={{ flex: 1 }}
                        value={em.type}
                        onChange={(e) =>
                          updateEmail(idx, "type", e.target.value)
                        }
                      >
                        <option value="">Select</option>
                        <option value="Primary">Primary</option>
                        <option value="Secondary">Secondary</option>
                      </select>
                      <div className="ms-2">
                        {idx === 0 ? (
                          <Button
                            className="btn-style"
                            variant="outline-primary"
                            size="sm"
                            onClick={addEmail}
                          >
                            <img src={Add} alt="add" className="add-icon" />
                          </Button>
                        ) : (
                          <Button
                            className="btn-style "
                            onClick={() => removeEmail(idx)}
                          >
                            <img
                              src={Delete}
                              alt="delete"
                              className="delete-icon"
                            />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <ErrorMessage message={validations.emails} />
                </div>
              </Col>

              <Col md={12} className="mt-3">
                <label className="field-label">Description</label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="Enter here..."
                  value={description}
                  onChange={(e) =>
                    setDescription(sanitizeTextarea(e.target.value))
                  }
                />
                <ErrorMessage message={validations.description} />
              </Col>
            </Row>
          </div>
          <div className="col-12 mt-3">
            <div className="contact-card text-start">
              <h5 className="card-title">Tags</h5>

              <div className="tag-list">
                {tags.length === 0 && (
                  <div className="tag-empty align-center">
                    Want to add tags for this contact person?
                  </div>
                )}

                {tags.map((tg, idx) => (
                  <div className="tag-chip" key={idx}>
                    <span className="tag-name">{tg}</span>
                    <button
                      className="tag-remove"
                      onClick={() => removeTag(idx)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div className="tag-input-row mt-3 d-flex align-items-center justify-content-start gap-2">
                <input
                  className="form-control tag-input"
                  placeholder="Add tag"
                  value={newTag}
                  onChange={(e) => setNewTag(sanitizeTextarea(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                />
                <Button variant="primary" onClick={addTag}>
                  Add Tag
                </Button>
              </div>
            </div>
          </div>

          <div className="col-12 mt-3">
            <div className="contact-card">
              <h5 className="card-title">Social Media Links</h5>
              <div className="sub-card">
                {socialLinks.map((s, idx) => (
                  <div className="sub-row d-flex align-items-center" key={idx}>
                    <input
                      className="form-control"
                      placeholder="Title"
                      value={s.title}
                      onChange={(e) =>
                        updateSocial(idx, "title", e.target.value)
                      }
                    />
                    <input
                      className="form-control ms-2"
                      placeholder="Paste link"
                      value={s.url}
                      onChange={(e) =>
                        updateSocial(idx, "url", sanitizeInput(e.target.value))
                      }
                    />
                    <div className="ms-2">
                      {idx === 0 ? (
                        <Button
                          className="btn-style"
                          variant="outline-primary"
                          size="sm"
                          onClick={addSocial}
                        >
                          <img src={Add} alt="add" className="add-icon" />
                        </Button>
                      ) : (
                        <Button
                          className="btn-style"
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeSocial(idx)}
                        >
                          <img
                            src={Delete}
                            alt="delete"
                            className="delete-icon"
                          />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4 col-sm-12">
          <div className="contact-card">
            <h5 className="card-title">Professional details</h5>
            <Row>
              <Col md={12}>
                <label className="field-label">
                  Designation<span className="required">*</span>
                </label>
                <SearchWithFreeTextAPI
                  onDropdownChange={handleDesignationDropdownChange}
                  onChangeSearch={handleDesignationOnChange}
                  apiUrl={apiAccUrl + `get_designation`}
                  params={{}}
                  searchLimit={0}
                  placeholder={"Search designation"}
                  selectedValue={designationText}
                />
                {/* <input
                  className="form-control"
                  placeholder="Enter here..."
                  value={designation}
                  onChange={(e) => setDesignation(sanitizeInput(e.target.value))}
                /> */}
                <ErrorMessage message={validations.designation} />
              </Col>

              <Col md={12} className="mt-3">
                <label className="field-label">Department</label>
                <SearchWithFreeTextAPI
                  onDropdownChange={handleDepartmentDropdownChange}
                  onChangeSearch={handleDepartmentOnChange}
                  apiUrl={apiAccUrl + `get_department`}
                  params={{}}
                  searchLimit={0}
                  placeholder={"Search department"}
                  selectedValue={departmentText}
                />
                <ErrorMessage message={validations.department} />
              </Col>

              <Col md={12} className="mt-3">
                <label className="field-label">Industry Type</label>
                {/* <select
                  className="form-select"
                  value={industry}
                  onChange={(e) => setIndustryOption(sanitizeInput(e.target.value))}
                >
                  <option value="">Select</option>
                </select> */}
                <SearchWithAPI
                  onDropdownChange={handleIndustryDropdownChange}
                  apiUrl={apiUrl + "lead/industry_autosuggestion"}
                  params={{}}
                  placeholder={"Select industry"}
                  selectedOption={industryOption}
                />
                <ErrorMessage message={validations.industry_type} />
              </Col>

              <Col md={12} className="mt-3">
                <label className="field-label">Contact Type</label>
                <div style={{ display: "flex", gap: "20px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={linkAccounts}
                      onChange={(e) => {
                        setLinkAccounts(e.target.checked);
                        if (!e.target.checked) setAccountOptions([]);
                      }}
                    />
                    Account
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={linkLeads}
                      onChange={(e) => {
                        setLinkLeads(e.target.checked);
                        if (!e.target.checked) setLeadOptions([]);
                      }}
                    />
                    Lead
                  </label>
                </div>
                <ErrorMessage message={validations.contact_type} />
              </Col>

              {linkLeads && (
                <Col md={12} className="mt-3">
                  <label className="field-label">Leads</label>
                  <AsyncSelect
                    isMulti
                    cacheOptions
                    defaultOptions
                    value={leadOptions}
                    loadOptions={loadLeadOptions}
                    onChange={(options) => setLeadOptions(options || [])}
                    placeholder="Select lead(s)"
                    styles={multiselectCustomStyles}
                  />
                  <ErrorMessage message={validations.lead_id} />
                </Col>
              )}

              {linkAccounts && (
                <Col md={12} className="mt-3">
                  <label className="field-label">Accounts</label>
                  <AsyncSelect
                    isMulti
                    cacheOptions
                    defaultOptions
                    value={accountOptions}
                    loadOptions={loadAccountOptions}
                    onChange={(options) => setAccountOptions(options || [])}
                    placeholder="Select account(s)"
                    styles={multiselectCustomStyles}
                  />
                  <ErrorMessage message={validations.account_id} />
                </Col>
              )}
            </Row>
          </div>

          <div className="contact-card mt-3">
            <h5 className="card-title">Location</h5>
            <label className="field-label">Address Title</label>
            <input
              className="form-control"
              placeholder="Enter here..."
              value={addressTitle}
              onChange={(e) =>
                setAddressTitle(sanitizeTextarea(e.target.value))
              }
            />
            <ErrorMessage message={validations.address_title} />

            <label className="field-label mt-3">Address</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Enter here..."
              value={address}
              onChange={(e) => setAddress(sanitizeTextarea(e.target.value))}
            />
            <ErrorMessage message={validations.address} />

            <Row className="mt-3">
              {/*  <Col md={6}>
                <input
                  className="form-control"
                  placeholder="Area"
                  value={area}
                  onChange={(e) => setArea(sanitizeInput(e.target.value))}
                />
                <ErrorMessage message={validations.area} />
              </Col> */}
              <Col md={12} className="mt-3">
                <SearchWithAPI
                  onDropdownChange={handleCountryDropdownChange}
                  apiUrl={apiAccUrl + "get_country"}
                  params={{}}
                  placeholder={"Select country"}
                  selectedOption={countryOption}
                />
                <ErrorMessage message={validations.country_id} />
              </Col>
              <Col md={12} className="mt-3">
                <SearchWithAPI
                  onDropdownChange={handleStateDropdownChange}
                  apiUrl={apiAccUrl + "get_state"}
                  params={{
                    country_id:
                      countryOption != null ? countryOption.value : "",
                  }}
                  placeholder={"Select state"}
                  selectedOption={stateOption}
                />

                <ErrorMessage message={validations.state_id} />
              </Col>

              <Col md={12} className="mt-3">
                <SearchWithAPI
                  onDropdownChange={handleCityDropdownChange}
                  apiUrl={apiAccUrl + "get_city"}
                  params={{
                    state_id: stateOption != null ? stateOption.value : "",
                  }}
                  placeholder={"Select city"}
                  selectedOption={cityOption}
                />
                <ErrorMessage message={validations.city_id} />
              </Col>
            </Row>

            <input
              className="form-control mt-3"
              placeholder="Zip code"
              value={zipCode}
              onChange={(e) => setZipCode(sanitizeInput(e.target.value))}
            />
            <ErrorMessage message={validations.zip_code} />
          </div>

          <div className="contact-card mt-3">
            <h5 className="card-title">Notes</h5>
            <textarea
              className="form-control"
              rows={4}
              placeholder="Enter here..."
              value={notes}
              onChange={(e) => setNotes(sanitizeTextarea(e.target.value))}
            />
            <ErrorMessage message={validations.notes} />
          </div>

          <div className="contact-card mt-3">
            <h5 className="card-title">Attachments</h5>
            <div className="mb-3">
              {attachments.map((f, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 16px",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E5E5",
                    borderRadius: "12px",
                    marginBottom: "12px",
                  }}
                >
                  {/* File Icon */}
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      backgroundColor: "#FFF5F5",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {getFileIcon(f.name)}
                  </div>

                  {/* File Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                        color: "#172B4D",
                        marginBottom: "2px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {f.file_name || f.name}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#8790A1",
                        fontWeight: "400",
                      }}
                    >
                      {formatFileSize(f.file_size || f.size) ?? ""}
                    </div>
                  </div>

                  <button
                    onClick={() => removeAttachment(i)}
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      border: "1px solid #E5E5E5",
                      backgroundColor: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#FFF5F5";
                      e.currentTarget.style.borderColor = "#FF4444";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#FFFFFF";
                      e.currentTarget.style.borderColor = "#E5E5E5";
                    }}
                  >
                    <span
                      style={{
                        fontSize: "18px",
                        color: "#FF4444",
                        fontWeight: "500",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </span>
                  </button>
                </div>
              ))}
              {attachmentsUrl.map((f, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 16px",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E5E5",
                    borderRadius: "12px",
                    marginBottom: "12px",
                  }}
                >
                  {/* File Icon */}
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      backgroundColor: "#FFF5F5",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {getFileIcon(f.name)}
                  </div>

                  {/* File Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                        color: "#172B4D",
                        marginBottom: "2px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {f.name ?? ""}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#8790A1",
                        fontWeight: "400",
                      }}
                    >
                      {f.size ? formatFileSize(f.size) : ""}
                    </div>
                  </div>

                  <button
                    onClick={() => downloadAttachment(f.filepath)}
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      border: "1px solid #E5E5E5",
                      backgroundColor: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                      transition: "all 0.2s",
                      marginRight: "8px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#F0F4FF";
                      e.currentTarget.style.borderColor = "#3D4AF5";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#FFFFFF";
                      e.currentTarget.style.borderColor = "#E5E5E5";
                    }}
                  >
                    <FaDownload
                      style={{
                        fontSize: "14px",
                        color: "#3D4AF5",
                      }}
                    />
                  </button>

                  <button
                    onClick={() => removeAttachmentUrl(i, f.id)}
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      border: "1px solid #E5E5E5",
                      backgroundColor: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                      transition: "all 0.2s",
                      paddingBottom: "6px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#FFF5F5";
                      e.currentTarget.style.borderColor = "#FF4444";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#FFFFFF";
                      e.currentTarget.style.borderColor = "#E5E5E5";
                    }}
                  >
                    <span
                      style={{
                        fontSize: "18px",
                        color: "#FF4444",
                        fontWeight: "500",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </span>
                  </button>
                </div>
              ))}
            </div>

            <div
              className={`attachment-drop ${isDragging ? "dragging" : ""}`}
              onClick={() => attachInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                multiple
                style={{ display: "none" }}
                ref={attachInputRef}
                onChange={onAttachChange}
              />
              <div className="drop-label">
                Drag and drop or <u>Select a file</u>
              </div>
              <div className="drop-sub">
                You can add any type of files here.
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-end">
        <Toast
          className="d-inline-block m-1"
          bg={snackbarStatus === true ? "success" : "danger"}
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={3000}
          autohide
        >
          <Toast.Body style={{ color: "#ffffff" }}>
            {snackbarMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default ContactsCreate;
